/**
 * @fileoverview Component for linking a users first external account
 *
 * @author Ahsan Qureshi
 */
import {EXTERNAL_ACCOUNTS_LINK_HELP,
  NO_EXTERNAL_ACCOUNTS_HEADING,
  EXTERNAL_ACCOUNTS_LINK_ACCOUNT_MESSAGE,
  LINK_NEW_ACCOUNT_BUTTON,
  LINK_ORG_ACCOUNT_BUTTON,
  CREATE_ACCOUNT_BUTTON,
  NO_MANAGE_LINKED_ACCOUNT_PERMISSION_ERROR} from '/js/yext/externalaccounts/listingslaunch/copy';
import {Publisher} from '/js/yext/externalaccounts/models/publisher';
import {Button} from '/ui/components/button/button';
import {CalloutBanner,
  CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {Card} from '/ui/components/card/card';
import * as toast from '/ui/components/toast/toast';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import ChainLinkSvg
  from '/js/yext/externalaccounts/listingslaunch/icons/chainlink.svg';
import InternalOnlySvg from '/js/yext/externalaccounts/listingslaunch/icons/internal-only.svg';
import * as styles
  from '/js/yext/externalaccounts/listingslaunch/styles/listingslaunchexternalaccounts.module.scss';

/**
 *
 * @param {{
 *   publisher: Publisher,
 *   handleAddNewAccount: function(string),
 *   handleAddOrgAccount: function(),
 *   openPopup: function(string),
 *   hasManageLinkedAccountPermission: boolean,
 *   canLinkOrgAccount?: boolean,
 *   skipSetup?: boolean,
 *   enableCallout?: boolean,
 * }} props
 * @return {React.ReactNode}
 */
export default function LinkExternalAccount({skipSetup=false, enableCallout=true, ...props}) {
  const {
    publisher,
    handleAddNewAccount,
    handleAddOrgAccount,
    openPopup,
    hasManageLinkedAccountPermission,
    canLinkOrgAccount,
  } = props;

  const linkAccountUrl = publisher.oauthUrl;
  const createAccountUrl = publisher.signUpUrl ? publisher.signUpUrl
    : publisher.oauthUrl;

  const isSmalllViewport = useIsSmallViewport();

  const renderCreateButton = () => {
    return (
      <Button
        pendoId={'listings-launch-flow-linking-create-account'}
        chromeless={true}
        onClick={() =>
          hasManageLinkedAccountPermission
            ? openPopup(createAccountUrl)
            : toast.showErrorToast(
              <span>{NO_MANAGE_LINKED_ACCOUNT_PERMISSION_ERROR}</span>)
        }
        disabled={skipSetup}>
        {CREATE_ACCOUNT_BUTTON}
      </Button>
    );
  };

  const renderLinkOrgAccountButton = () => {
    return (
      <Button
        context="default"
        onClick={handleAddOrgAccount}>
        {LINK_ORG_ACCOUNT_BUTTON} <InternalOnlySvg />
      </Button>
    );
  };

  const renderLinkNewAccountButton = () => {
    return (
      <Button pendoId={'listings-launch-flow-linking-link-account'}
        context="default"
        onClick={() =>
          hasManageLinkedAccountPermission
            ? handleAddNewAccount(linkAccountUrl)
            : toast.showErrorToast(
              <span>{NO_MANAGE_LINKED_ACCOUNT_PERMISSION_ERROR}</span>)
        }
        disabled={skipSetup}>
        {LINK_NEW_ACCOUNT_BUTTON}
      </Button>
    );
  };

  const renderMobileCardBody = () => {
    return (
      <div className={styles.mobileLinkExternalAccountContent}>
        <div className={styles.svgContainer}>
          <ChainLinkSvg/>
        </div>
        <div className={styles.ctaMobile}>
          <h3>{NO_EXTERNAL_ACCOUNTS_HEADING}</h3>
          <div>
            {EXTERNAL_ACCOUNTS_LINK_ACCOUNT_MESSAGE(publisher['name'])}
          </div>
          <div className={styles.ctaMobileLinkButton}>
            {renderLinkNewAccountButton()}
          </div>
          {canLinkOrgAccount && hasOrgAccount(publisher)
            ? renderLinkOrgAccountButton()
            : renderCreateButton()
          }
        </div>
      </div>
    );
  };

  return (
    <div className={styles.linkExternalAccount}>
      {enableCallout
        && <CalloutBanner type={CalloutBannerType.NEUTRAL}>
          {EXTERNAL_ACCOUNTS_LINK_HELP(publisher['name'])}
        </CalloutBanner>
      }
      <Card>
        <Card.Body>
          {isSmalllViewport
            ? renderMobileCardBody()
            : (<div className={styles.linkExternalAccountContent}>
              <div className={styles.svgContainer}>
                <ChainLinkSvg/>
              </div>
              <div className={styles.cta}>
                <h3>{NO_EXTERNAL_ACCOUNTS_HEADING}</h3>
                <div>
                  {EXTERNAL_ACCOUNTS_LINK_ACCOUNT_MESSAGE(publisher['name'])}
                </div>
                <div className={styles.ctaButtons}>
                  {renderLinkNewAccountButton()}
                  {canLinkOrgAccount && hasOrgAccount(publisher)
                    ? renderLinkOrgAccountButton()
                    : (
                      <div className={styles.ctaCreateAccountButton}>
                        {renderCreateButton()}
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
            )}

        </Card.Body>
      </Card>
    </div>
  );
}

/**
 *
 * @param {Publisher} publisher
 * @return {boolean}
 */
function hasOrgAccount(publisher) {
  return (publisher.id == 715 /* Google */);
}
